import React, { createContext, useContext } from "react";
import classnames from "classnames";
import Heading, { Props as HeadingProps } from "@unum/heading";
import SmallHeading, {
  Colour as SmallHeadingColour
} from "@unum/small-heading";
import Align from "@unum/align";
import styles from "./Section.module.scss";

export type Curve = boolean | "grey";

export type Spacing = "both" | "top" | "bottom" | "none" | "half";

export type BackgroundColour =
  | "transparent"
  | "grey"
  | "blue"
  | "dark-blue"
  | "light-blue"
  | "orange"
  | "gradient"
  | "primary_blue"
  | "blue_d1"
  | "blue_l2"
  | "slate"
  | "slate_l1"
  | "slate_l2"
  | "slate_l3"
  | "primary_orange"
  | "orange_d2"
  | "orange_l2"
  | "orange_l3"
  | "orange_l4"
  | "lawn"
  | "lawn_l3"
  | "pool"
  | "pool_l3"
  | "gold"
  | "sky"
  | "sky_l3"
  | "kiwi"
  | "kiwi_l3"
  | "warm_neutral"
  | "cool_neutral_l1"
  | "default_gradient"
  | "lawn_gradient"
  | "pool_gradient"
  | "primary_blue_gradient"
  | "orange_gradient";

export type Alignment = "left" | "center";

export type Props = {
  children: React.ReactNode;
  backgroundColour?: BackgroundColour;
  spacing?: Spacing;
  topCurve?: Curve;
  bottomCurve?: Curve;
  align?: Alignment;
  className?: string;
  allowNested?: boolean;
};

export type SectionHeadingProps = HeadingProps & {
  isUnderlined?: boolean;
  overline?: React.ReactNode;
  isAllCaps?: boolean;
  isWide?: boolean;
};

type Context = {
  isNested: boolean;
  backgroundColour?: BackgroundColour;
  align?: Alignment;
};

const SectionContext = createContext<Context>({
  isNested: false
});

const useWhiteText = (backgroundColour: BackgroundColour) => {
  return [
    "dark-blue",
    "gradient",
    "primary_blue",
    "blue_d1",
    "blue_l2",
    "slate",
    "primary_orange",
    "orange_d2",
    "lawn",
    "pool",
    "sky",
    "warm_neutral",
    "default_gradient",
    "lawn_gradient",
    "pool_gradient",
    "primary_blue_gradient",
    "orange_gradient"
  ].includes(backgroundColour);
};

const Section = ({
  children,
  backgroundColour = "transparent",
  spacing,
  topCurve,
  bottomCurve,
  align = "center",
  className,
  allowNested = false
}: Props) => {
  const { isNested } = useContext(SectionContext);

  if (isNested && !allowNested) {
    throw new Error("Section: You should not nest sections.");
  }

  return (
    <SectionContext.Provider
      value={{ isNested: true, backgroundColour, align }}
    >
      <div
        className={classnames(styles["Section"], "section", className, {
          [styles["Section--white-text"]]: useWhiteText(backgroundColour),
          [styles[`Section--${backgroundColour}`]]:
            backgroundColour !== "transparent" &&
            backgroundColour !== "grey" &&
            backgroundColour !== "blue" &&
            backgroundColour !== "dark-blue" &&
            backgroundColour !== "orange",
          [`section--${backgroundColour}`]:
            backgroundColour !== "transparent" &&
            backgroundColour !== "light-blue" &&
            backgroundColour !== "gradient",
          "section--padding-bottom-out":
            spacing === "none" || spacing === "top",
          "section--padding-top-out":
            spacing === "none" || spacing === "bottom",
          [styles["Section--padding-half"]]: spacing === "half",
          "section--curve-top": topCurve,
          "section--curve-bottom": bottomCurve,
          "section--curve-top--grey": topCurve === "grey",
          "section--curve-bottom--grey": bottomCurve === "grey"
        })}
      >
        {children}
      </div>
    </SectionContext.Provider>
  );
};

const backgroundOverlineMap: Record<BackgroundColour, SmallHeadingColour> = {
  transparent: "grey",
  grey: "grey",
  blue: "white",
  "dark-blue": "white",
  "light-blue": "grey",
  orange: "grey",
  gradient: "white",
  primary_blue: "white",
  blue_d1: "white",
  blue_l2: "white",
  slate: "white",
  slate_l1: "grey",
  slate_l2: "grey",
  slate_l3: "grey",
  primary_orange: "white",
  orange_d2: "white",
  orange_l2: "grey",
  orange_l3: "grey",
  orange_l4: "grey",
  lawn: "white",
  lawn_l3: "grey",
  pool: "white",
  pool_l3: "grey",
  gold: "grey",
  sky: "white",
  sky_l3: "grey",
  kiwi: "grey",
  kiwi_l3: "grey",
  warm_neutral: "white",
  cool_neutral_l1: "grey",
  default_gradient: "white",
  lawn_gradient: "white",
  pool_gradient: "white",
  primary_blue_gradient: "white",
  orange_gradient: "white"
};

const SectionHeading = ({
  children,
  overline,
  className,
  isUnderlined,
  isAllCaps,
  isWide,
  ...headingProps
}: SectionHeadingProps) => {
  const { backgroundColour, align } = useContext(SectionContext);

  return (
    <div className="row">
      <div
        className={classnames("col-xs-12", {
          "col-md-8 col-md-offset-2": align === "center" && !isWide,
          "col-md-8": align === "left" && !isUnderlined && !isWide,
          "col-md-12": (align === "left" && isUnderlined) || isWide
        })}
      >
        <Align direction={align}>
          <Heading
            level={isUnderlined ? 5 : 3}
            className={classnames(className, styles["heading"], {
              [styles["heading--underlined"]]: isUnderlined,
              [styles["heading--all-caps"]]: isAllCaps,
              [styles["heading--white"]]: useWhiteText(backgroundColour)
            })}
            {...headingProps}
          >
            {overline && (
              <SmallHeading
                className={styles["overline"]}
                colour={backgroundOverlineMap[backgroundColour]}
                component="div"
              >
                {overline}
              </SmallHeading>
            )}
            {children}
          </Heading>
        </Align>
      </div>
    </div>
  );
};

const SectionSubtitle = ({
  children,
  size
}: {
  children: React.ReactNode;
  size?: "default" | "large";
}) => {
  const { align } = useContext(SectionContext);

  return (
    <Align
      direction={align}
      component="div"
      className={classnames(styles["subtitle"], styles[`subtitle--${size}`], {
        "col-md-8 col-md-offset-2": align === "center"
      })}
    >
      {children}
    </Align>
  );
};

type FooterProps = {
  children: React.ReactNode;
};

const SectionFooter = ({ children }: FooterProps) => {
  return <div className={styles["footer"]}>{children}</div>;
};

Section.Heading = SectionHeading;
Section.Subtitle = SectionSubtitle;
Section.Content = Heading.Content;
Section.Footer = SectionFooter;

export default Section;
